import { OTHER_OPERATOR_SLUG } from '@ev/shared'
import { useId, useMemo } from 'react'
import { useStrings } from '../../i18n'
import { hasPublishedResults, operatorMatchCount } from '../../state/results'
import { selectFilters, selectResults, useAppStore } from '../../state/store'
import { ChargeCards } from './components/ChargeCards'
import { FacetGroup } from './components/FacetGroup'
import { PowerSlider } from './components/PowerSlider'
import { SearchBox } from './components/SearchBox'
import { UntrackedOperators } from './components/UntrackedOperators'
import {
  CONNECTOR_OPTIONS,
  CONTACTLESS_PAYMENT_METHOD,
  CURRENT_TYPE_OPTIONS,
  countActiveFilters,
  FREE_POINT_AVAILABILITY,
  OPERATOR_OPTIONS,
  VOLTAGE_CLASS_OPTIONS,
} from './facets'

/**
 * The panel contents, shared by the desktop side panel and the mobile sheet.
 *
 * It owns no state of its own: every control reads from and writes to the store,
 * so the sheet can be dismissed and reopened, or the whole panel remounted on a
 * viewport change, without anything being lost.
 *
 * The counts beside each row come from the map's published results, not from a
 * second pass over the data here. That is the point of the results slice: the
 * number next to "Tauron" and the dots on the canvas are the same computation.
 */
export const FilterPanelBody = () => {
  const strings = useStrings()
  const emptyTitleId = useId()

  const filters = useAppStore(selectFilters)
  const results = useAppStore(selectResults)
  const toggleFilter = useAppStore((state) => state.toggleFilter)
  const setMinPowerKw = useAppStore((state) => state.setMinPowerKw)
  const resetFilters = useAppStore((state) => state.resetFilters)

  const published = hasPublishedResults(results)
  const activeCount = countActiveFilters(filters)
  const nothingMatches = published && results.matching === 0

  const operatorOptions = useMemo(
    () =>
      OPERATOR_OPTIONS.map((brand) => ({
        value: brand.slug,
        // The registry names the `other` bucket in Polish; everything else is a
        // brand name, which is the same word in both languages.
        label:
          brand.slug === OTHER_OPERATOR_SLUG ? strings.filters.otherOperators : brand.displayName,
        color: brand.color,
        count: published ? operatorMatchCount(results, brand.slug) : undefined,
      })),
    [strings, results, published],
  )

  const connectorOptions = useMemo(
    () =>
      CONNECTOR_OPTIONS.map((standard) => ({
        value: standard,
        label: strings.connector[standard],
      })),
    [strings],
  )

  const currentTypeOptions = useMemo(
    () =>
      CURRENT_TYPE_OPTIONS.map((currentType) => ({
        value: currentType,
        label: strings.currentType[currentType],
      })),
    [strings],
  )

  const voltageOptions = useMemo(
    () =>
      VOLTAGE_CLASS_OPTIONS.map((voltageClass) => ({
        value: voltageClass,
        label: strings.voltageClass[voltageClass],
      })),
    [strings],
  )

  return (
    <div className="filters">
      <SearchBox />

      <div className="filters__summary">
        <p className="filters__active">
          {activeCount === 0
            ? strings.filters.noneActive
            : strings.filters.activeCount(activeCount)}
        </p>
        <button
          className="ev-button ev-button--quiet"
          type="button"
          disabled={activeCount === 0}
          onClick={resetFilters}
        >
          {strings.filters.clearAll}
        </button>
      </div>

      {!nothingMatches ? null : (
        <div className="filters__empty" role="status" aria-labelledby={emptyTitleId}>
          <p className="filters__empty-title" id={emptyTitleId}>
            {strings.filters.emptyTitle}
          </p>
          <p className="filters__empty-body">{strings.search.noResults}</p>
          <button className="ev-button ev-button--primary" type="button" onClick={resetFilters}>
            {strings.filters.clearAll}
          </button>
        </div>
      )}

      <ChargeCards />

      <FacetGroup
        facet="operators"
        className="facet--operators"
        legend={strings.filters.operators}
        options={operatorOptions}
        selected={filters.operators}
        onToggle={toggleFilter}
        countLabel={strings.filters.matchingSites}
      >
        <UntrackedOperators />
      </FacetGroup>

      <FacetGroup
        facet="connectors"
        legend={strings.filters.connectors}
        options={connectorOptions}
        selected={filters.connectors}
        onToggle={toggleFilter}
        countLabel={strings.filters.matchingSites}
      />

      <FacetGroup
        facet="currentTypes"
        legend={strings.filters.currentType}
        options={currentTypeOptions}
        selected={filters.currentTypes}
        onToggle={toggleFilter}
        countLabel={strings.filters.matchingSites}
      />

      <PowerSlider minPowerKw={filters.minPowerKw} onChange={setMinPowerKw} />

      <FacetGroup
        facet="voltageClasses"
        legend={strings.filters.voltageClass}
        options={voltageOptions}
        selected={filters.voltageClasses}
        onToggle={toggleFilter}
        countLabel={strings.filters.matchingSites}
      />

      <FacetGroup
        facet="availability"
        legend={strings.filters.availability}
        options={[{ value: FREE_POINT_AVAILABILITY, label: strings.filters.freePointsOnly }]}
        selected={filters.availability}
        onToggle={toggleFilter}
        countLabel={strings.filters.matchingSites}
      />

      <FacetGroup
        facet="paymentMethods"
        legend={strings.filters.paymentMethod}
        options={[{ value: CONTACTLESS_PAYMENT_METHOD, label: strings.filters.contactlessCard }]}
        selected={filters.paymentMethods}
        onToggle={toggleFilter}
        countLabel={strings.filters.matchingSites}
      />
    </div>
  )
}
